/* Team: the operators who sold through the CRM, ranked by Revenue (their sales)
   or Chats, optionally with the AI as a row. Same rows as Statistics → Team. */

import { useTranslation } from 'react-i18next';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import { Icon } from '../../components/ChatAssistant/compact/kit';
import { fmt, ledgerMoney, pick, usd } from '../../components/Statistics/unified/model';
import { periodRange, useDash, useSource } from '../context';
import type { WidgetOf } from '../model';
import { BodyNote, WidgetFrame, type FrameProps } from '../WidgetFrame';
import { useDataState } from '../frameUtils';
import { useMetaText } from './shared';

interface Row { id: string; name: string; initials: string; ai: boolean; revenue: number; chats: number | null }

const initials = (name: string) => name.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase() || '?';

export function Team(p: FrameProps & { widget: WidgetOf<'team'> }) {
  const { t } = useTranslation('dashboardWidgets');
  const { today, money } = useDash();
  const meta = useMetaText();
  const state = useDataState();
  const c = p.widget.cfg;
  const range = periodRange(c.per, today);
  const stats = useSource('stats', range);
  const chat = useSource('chat', c.ai ? range : null);

  let rows: Row[] = (stats.data?.team ?? []).map((r) => ({
    id: r.operator_id,
    name: r.operator_username || r.operator_id,
    initials: initials(r.operator_username || '?'),
    ai: false,
    revenue: ledgerMoney(r.total_sales, money),
    chats: r.chats_count,
  }));
  if (c.ai && stats.data) {
    rows.push({ id: 'ai', name: t('team.ai'), initials: '', ai: true, revenue: pick(stats.data.money.sellers.ai, money), chats: chat.data ? chat.data.totals.chats_activated : null });
  }
  rows = rows.sort((a, b) => (c.rank === 'rev' ? b.revenue - a.revenue : (b.chats ?? -1) - (a.chats ?? -1)));
  const note = state({ error: stats.error, noAccess: stats.noAccess, hasData: !!stats.data, reload: stats.reload });
  const chatsText = (n: number | null) => (n == null ? '—' : t('team.chats', { count: n, n: fmt(n) }));

  return (
    <WidgetFrame {...p} meta={meta.join([meta.period(c.per)])} link={{ to: '/statistics?view=team', label: t('team.link') }} fit={note ? undefined : rows.length ? rows.length * 36 + 16 : 110}>
      {note ?? (!rows.length ? (
        <BodyNote icon="users">{t('team.empty')}</BodyNote>
      ) : (
        <div className="dbw-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 16px', display: 'flex', flexDirection: 'column' }}>
          {rows.map((r, i) => (
            <div key={r.id} style={{ height: 36, flex: 'none', display: 'flex', alignItems: 'center', gap: 10, font: f(400, 12.5), color: C.body }}>
              <span style={{ width: 16, flex: 'none', font: f(600, 11.5), color: C.muted }}>{i + 1}</span>
              <span style={{ width: 24, height: 24, flex: 'none', borderRadius: 99, background: r.ai ? C.tint20 : C.surface, border: `1px solid ${C.border}`, boxSizing: 'border-box', color: C.purplePale, font: f(700, 9.5), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {r.ai ? <Icon name="zap" size={12} /> : r.initials}
              </span>
              <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
              <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{c.rank === 'rev' ? chatsText(r.chats) : usd(r.revenue)}</span>
              <span style={{ width: 64, flex: 'none', textAlign: 'right', font: f(600, 12.5), color: C.text }}>{c.rank === 'rev' ? usd(r.revenue) : r.chats == null ? '—' : fmt(r.chats)}</span>
            </div>
          ))}
        </div>
      ))}
    </WidgetFrame>
  );
}
